import { useState, useEffect, useCallback } from 'react';
import { SideNav } from '../components/shared/SideNav';
import { AIChat } from '../components/shared/AIChat';
import { AIInsights } from '../components/shared/AIInsights';
import { api, formatCurrency } from '../lib/api';
import { useFileInput, compressImage } from '../components/shared/FileUpload';
import ChefBudget from './chef/Budget';
import ChefFactures from './chef/Factures';
import ChefPertes from './chef/Pertes';
import ChefManuel from './chef/Manuel';
import ChefCarte from './chef/Carte';
import ChefCommande from './chef/Commande';
import ChefCouts from './chef/CoutPlats';
import ChefRecettes from './chef/Recettes';
import ChefMercuriales from './chef/Mercuriales';
import ChefCongelateur from './chef/Congelateur';
import ChefMenusGroupe from './chef/MenusGroupe';
import { Camera, Loader2, X, Check, ChefHat, Wine, Package, AlertTriangle, Mail, Lock, Eye, EyeOff, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

// ═══ Chef Login Screen ═══
function ChefLoginScreen({ onSuccess }) {
  const [mode, setMode] = useState('login'); // login, register
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Erreur'); setLoading(false); return; }
      if (data.user?.role !== 'chef' && data.user?.role !== 'patron') { setError('Ce compte n\'est pas un compte Chef'); setLoading(false); return; }
      sessionStorage.setItem('chef_auth', 'true');
      sessionStorage.setItem('chef_token', data.token);
      sessionStorage.setItem('chef_user', JSON.stringify(data.user));
      onSuccess(data.user);
    } catch { setError('Erreur de connexion'); }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/api/auth/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password, name }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Erreur'); setLoading(false); return; }
      setMode('pending');
    } catch { setError('Erreur d\'inscription'); }
    setLoading(false);
  };

  if (mode === 'pending') {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-[#1B2A52] to-[#0F1829]">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 w-full max-w-sm text-center border border-white/10">
          <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4"><Clock size={28} className="text-amber-400" /></div>
          <h2 className="text-lg font-bold text-white mb-2">Inscription enregistree !</h2>
          <p className="text-sm text-white/60 mb-6">Votre compte est en attente de validation par le patron. Vous serez prevenu quand il sera active.</p>
          <Button onClick={() => setMode('login')} variant="outline" className="border-white/20 text-white/70 hover:bg-white/5">Retour a la connexion</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-[#1B2A52] to-[#0F1829]" data-testid="chef-login">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 w-full max-w-sm border border-white/10">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-[#C9A84C]/20 flex items-center justify-center mx-auto mb-3"><ChefHat size={28} className="text-[#C9A84C]" /></div>
          <h1 className="text-xl font-bold text-white">Espace Chef</h1>
          <p className="text-xs text-white/40 mt-1">Le Mas — Restaurant & Traiteur</p>
        </div>

        <form onSubmit={mode === 'login' ? handleLogin : handleRegister} className="space-y-3">
          {mode === 'register' && (
            <div>
              <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1">Nom complet</label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Jean Dupont" required
                className="bg-white/5 border-white/10 text-white placeholder:text-white/20" data-testid="chef-name" />
            </div>
          )}
          <div>
            <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1">Email</label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="chef@lemas.fr" required
                className="bg-white/5 border-white/10 text-white placeholder:text-white/20 pl-9" data-testid="chef-email" />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1">Mot de passe</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <Input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••" required minLength={6}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/20 pl-9 pr-9" data-testid="chef-password" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/50">
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          {error && <p className="text-xs text-red-400 bg-red-400/10 rounded-lg p-2" data-testid="chef-error">{error}</p>}

          <Button type="submit" disabled={loading} className="w-full bg-[#C9A84C] hover:bg-[#B08A3E] text-white font-bold" data-testid="chef-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : mode === 'login' ? 'Se connecter' : 'Creer mon compte'}
          </Button>

          <div className="text-center">
            {mode === 'login' ? (
              <button type="button" onClick={() => setMode('register')} className="text-xs text-white/40 hover:text-white/60 flex items-center justify-center gap-1 mx-auto">
                <UserPlus size={12} /> Pas encore de compte ? S'inscrire
              </button>
            ) : (
              <button type="button" onClick={() => setMode('login')} className="text-xs text-white/40 hover:text-white/60">
                Deja un compte ? Se connecter
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

const Clock = ({ size, className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
);

const ChefApp = () => {
  const [authenticated, setAuthenticated] = useState(() =>
    sessionStorage.getItem('chef_auth') === 'true'
  );
  const [chefUser, setChefUser] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('chef_user') || 'null'); } catch { return null; }
  });
  const [activeTab, setActiveTab] = useState('budget');
  const [chefAccess, setChefAccess] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  useEffect(() => {
    api.get('/settings/chef-access').then(res => {
      setChefAccess(res.data);
      // If current tab is disabled, switch to first enabled
      if (res.data?.pages) {
        const pages = res.data.pages;
        if (pages[activeTab] && pages[activeTab].enabled === false) {
          const firstEnabled = Object.entries(pages).find(([, v]) => v.enabled !== false);
          if (firstEnabled) setActiveTab(firstEnabled[0]);
        }
      }
    }).catch(() => {});
  }, []);

  const handleQuickScan = useCallback(async (file) => {
    if (!file) return;
    setScanLoading(true);
    setScanResult(null);
    try {
      const compressed = await compressImage(file);
      const formData = new FormData();
      formData.append('file', compressed);
      formData.append('source', 'chef');
      const res = await api.post('/analyze-invoice', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      const data = res.data;
      const nbProd = data.produits?.length || 0;
      toast.success(`${data.fournisseur} — ${nbProd} produit${nbProd > 1 ? 's' : ''}`);
      setScanResult(data);
      setActiveTab('factures');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur scan');
    }
    setScanLoading(false);
  }, []);

  const floatScanRef = useFileInput(handleQuickScan);

  if (!authenticated) {
    return <ChefLoginScreen onSuccess={(user) => { setAuthenticated(true); setChefUser(user); }} />;
  }

  const handleLogout = () => {
    sessionStorage.removeItem('chef_auth');
    sessionStorage.removeItem('chef_token');
    sessionStorage.removeItem('chef_user');
    setAuthenticated(false);
    setChefUser(null);
  };

  const isPageEnabled = (id) => {
    if (!chefAccess?.pages) return true;
    return chefAccess.pages[id]?.enabled !== false;
  };

  const renderPage = () => {
    switch (activeTab) {
      case 'budget': return isPageEnabled('budget') ? <ChefBudget chefAccess={chefAccess} /> : null;
      case 'factures': return isPageEnabled('factures') ? <ChefFactures chefAccess={chefAccess} /> : null;
      case 'pertes': return isPageEnabled('pertes') ? <ChefPertes /> : null;
      case 'commande': return isPageEnabled('commande') ? <ChefCommande /> : null;
      case 'couts': return isPageEnabled('couts') ? <ChefCouts chefAccess={chefAccess} /> : null;
      case 'manuel': return isPageEnabled('manuel') ? <ChefManuel /> : null;
      case 'carte': return isPageEnabled('carte') ? <ChefCarte chefAccess={chefAccess} /> : null;
      case 'mercuriales': return isPageEnabled('mercuriales') ? <ChefMercuriales chefAccess={chefAccess} /> : null;
      case 'recettes': return isPageEnabled('recettes') ? <ChefRecettes /> : null;
      case 'congelateur': return <ChefCongelateur />;
      case 'menus-groupe': return <ChefMenusGroupe />;
      default: return isPageEnabled('budget') ? <ChefBudget chefAccess={chefAccess} /> : null;
    }
  };

  const showAI = !chefAccess?.fonctionnalites || chefAccess.fonctionnalites.acces_max?.enabled !== false;

  return (
    <div className="min-h-screen bg-[rgb(var(--lm-bg))] transition-colors duration-300">
      <SideNav activeTab={activeTab} setActiveTab={setActiveTab} role="chef" onLogout={handleLogout} chefAccess={chefAccess} />
      <div className="md:ml-52 pt-14 md:pt-0">
        <div className="max-w-6xl mx-auto p-4 md:p-6 lg:p-8">
          {showAI && <AIInsights page={activeTab} role="chef" />}
          {renderPage()}
        </div>
      </div>
      {showAI && <AIChat role="chef" page={activeTab} />}

      {/* Scan result toast */}
      {scanResult && (
        <div className="fixed bottom-24 right-6 z-50 w-72 lm-card p-3 border-[rgb(var(--lm-green))/0.3] bg-[rgb(var(--lm-surface))] shadow-xl animate-slide-up" data-testid="scan-result-float">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Check size={14} className="text-[rgb(var(--lm-green))]" />
              <span className="text-xs font-medium text-[rgb(var(--lm-green))]">Scanne</span>
            </div>
            <button onClick={() => setScanResult(null)} className="text-[rgb(var(--lm-text-muted))]"><X size={12} /></button>
          </div>
          <div className="flex justify-between items-center mb-1.5">
            <span className="font-medium text-sm truncate">{scanResult.fournisseur}</span>
            <span className="font-mono text-sm text-[rgb(var(--lm-green))]">{formatCurrency(scanResult.total_ttc)}</span>
          </div>
          <div className="flex gap-2 text-[10px]">
            {(scanResult.cuisine_ht > 0) && <span className="flex items-center gap-0.5"><ChefHat size={10} style={{ color: 'rgb(var(--lm-green))' }} />{formatCurrency(scanResult.cuisine_ht)}</span>}
            {(scanResult.boissons_ht > 0) && <span className="flex items-center gap-0.5"><Wine size={10} style={{ color: 'rgb(100,149,237)' }} />{formatCurrency(scanResult.boissons_ht)}</span>}
            {(scanResult.non_alimentaire_ht > 0) && <span className="flex items-center gap-0.5"><Package size={10} style={{ color: 'rgb(var(--lm-orange))' }} />{formatCurrency(scanResult.non_alimentaire_ht)}</span>}
          </div>
          {scanResult.produits?.length > 0 ? (
            <p className="text-[10px] text-emerald-400 mt-1">{scanResult.produits.length} produits extraits</p>
          ) : (
            <p className="text-[10px] text-amber-400 mt-1 flex items-center gap-1"><AlertTriangle size={10} />0 produits — re-scannez</p>
          )}
        </div>
      )}

      {/* Floating scan button — input overlay pour iOS */}
      {scanLoading ? (
        <div className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-green))] shadow-lg flex items-center justify-center" data-testid="chef-quick-scan-btn">
          <Loader2 size={24} className="animate-spin text-[rgb(var(--lm-green))]" />
        </div>
      ) : (
        <div className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[rgb(var(--lm-green))] shadow-lg flex items-center justify-center overflow-hidden" data-testid="chef-quick-scan-btn" title="Scanner une facture">
          <input
            ref={floatScanRef}
            type="file"
            accept="image/*"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            style={{ fontSize: '200px' }}
          />
          <Camera size={24} className="text-white pointer-events-none" />
        </div>
      )}
    </div>
  );
};

export default ChefApp;
